import moneyTransferVisuals from "../../media/moneyTransferVisuals";

const moneyTransfer = {
  "id": "money-transfer",
  "slug": "money-transfer",
  "category": "Applications",
  "navTitle": "Expanding into Money Transfer",
  "title": "Expanding into Money Transfer",
  "introduction": [
    "XE had built its reputation on providing accurate mid-market exchange rates. When the company expanded into international money transfer, we needed to introduce an entirely new service without undermining the experience millions of people already trusted.",
    "As UX lead, I worked with product, engineering, and marketing teams to determine how money transfer could become part of the XE experience while keeping currency conversion clear, familiar, and reliable."
  ],
  "challenge": [
    {
      "title": "Protecting a trusted experience",
      "paragraphs": [
        "For many customers, XE meant one thing: the mid-market exchange rate. Introducing tradeable rates, fees, and transaction details risked creating confusion and reducing trust."
      ]
    },
    {
      "title": "Supporting two different customer needs",
      "paragraphs": [
        "Some people visited XE simply to check exchange rates. Others wanted to send money internationally. The product needed to support both without forcing either audience into an experience they didn't want."
      ]
    },
    {
      "title": "Introducing a commercial service naturally",
      "paragraphs": [
        "Money transfer was an important business initiative, but it needed to feel like a logical extension of the product rather than an interruption."
      ]
    }
  ],
  "process": [
    {
      "title": "Understanding where confusion occurred",
      "paragraphs": [
        "Before introducing the new service, I wanted to understand how different ways of presenting exchange rates affected customer understanding."
      ],
      "bullets": [
        "Explored two interaction models for introducing money transfer.",
        "Compared showing both mid-market and tradeable rates together against presenting the trusted mid-market rate first and introducing transaction details later.",
        "Conducted usability testing to observe how customers interpreted each approach.",
        "Found that displaying both rates together created unnecessary confusion, particularly for existing XE users.",
        "Used the findings to guide the product direction."
      ],
      "closing": "The research gave us confidence to choose the simpler experience rather than assuming more information was better."
    },
    {
      "title": "Introducing the service progressively",
      "paragraphs": [
        "We designed the experience so customers encountered additional information only when it became relevant to the task they were completing."
      ],
      "bullets": [
        "Kept the familiar mid-market rate as the primary experience.",
        "Introduced transactional rates and fees only after customers chose to begin a money transfer.",
        "Added contextual explanations where customers needed additional information.",
        "Designed separate experiences for signed-in and anonymous users based on what each group needed to accomplish.",
        "Preserved the speed and simplicity of currency conversion for customers who had no intention of transferring money."
      ],
      "closing": "This allowed the new service to complement the existing product instead of competing with it."
    },
    {
      "title": "Refining the experience after launch",
      "paragraphs": [
        "We continued improving the experience through testing, analytics, and customer feedback."
      ],
      "bullets": [
        "Worked closely with engineering to ensure the experience remained responsive and technically reliable.",
        "Partnered with marketing to simplify messaging around money transfer.",
        "Used A/B testing to evaluate refinements and improve conversion.",
        "Reviewed customer feedback and behavioural data after launch to identify areas for improvement.",
        "Continued adjusting the experience as customer behaviour became better understood."
      ],
      "closing": "The work continued well beyond launch as we balanced customer expectations with business goals."
    }
  ],
  "outcomes": [
    {
      "title": "A successful product expansion",
      "description": "Money transfer became part of the XE experience without disrupting the trusted currency conversion tools customers relied on every day."
    },
    {
      "title": "Higher conversion",
      "description": "The new experience contributed to a 200% increase in new user conversions within six months."
    },
    {
      "title": "Maintained customer trust",
      "description": "The app continued to maintain a 4.5-star rating, reflecting that the expanded experience remained clear and easy to use."
    },
    {
      "title": "A stronger digital ecosystem",
      "description": "Currency conversion and money transfer complemented one another, creating a more complete financial experience while preserving XE's reputation for trusted exchange-rate information."
    }
  ],
  "reflection": [
    "This project reinforced that introducing a new feature isn't always the hardest part. Knowing where it belongs within an existing experience is often the bigger design challenge.",
    "The research confirmed that customers came to XE with different goals. Respecting those goals, rather than pushing everyone through the same journey, made it possible to expand the product without losing the simplicity and trust that had made it successful in the first place."
  ],
  "media": {
    "blocks": [
      {
        "id": "money-transfer-journey",
        "type": "carousel",
        "placement": "afterIntro",
        "items": moneyTransferVisuals,
        "layout": "mobileFocus",
        "options": {
          "frameMaxHeight": "min(64vh, 720px)"
        }
      }
    ]
  }
};

export default moneyTransfer;
